import { useCallback, useContext, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import {
  Accordion,
  Button,
  Form,
  Icon,
  Label,
  Popup,
  type SemanticCOLORS,
  type SemanticICONS,
  TextArea,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas/useIdeaQueryParams";
import { Checkbox } from "components/lib/UI";

import {
  formatImpact,
  impactMultipliers,
} from "../Ideas/ImpactStatements/statementUtils";
import type { OrganisationImpact } from "../Ideas/types";
import type { ILaneData } from "./Board";
import { DropdownLaneSettings } from "./LaneSettings";
import { MetaInfo } from "./MetaContainer";
import { ProjectBoardContext } from "./ProjectBoardContext";
import type {
  LaneImpactInput,
  ProjectBoardLanePartial,
  ProjectBoardLaneUpdateBody,
  ProjectBoardUser,
} from "./types";

const StyledLaneHeader = styled.div<{ $isSelecting?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;

  ${({ $isSelecting }) =>
    $isSelecting ?
      `
    >*:not(.selection-box) {
      opacity: 0.6;
      cursor: pointer;
    }
  `
    : null}
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  margin-right: 5px;
  h3 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    -moz-line-clamp: 2;

    font-size: 1.2em;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  span {
    font-weight: 375;
  }
`;

const LaneDescription = styled.p`
  color: gray;
  width: 100%;
  word-wrap: break-word;
  word-break: normal;
  white-space: normal;
  margin: 2.5px 0;
  text-align: left;
`;

const InvisibleButton = styled(Button)`
  &&& {
    background: #f1f2f4;
    &:hover {
      background: #e0e1e2;
    }
  }
`;

const Header = styled.div`
  display: flex !important;
  flex-direction: column !important;
  gap: 8px;
  background: rgb(241, 242, 244);
  padding: 10px 8px;
  .selection-box {
    opacity: 1;
    top: 0px;
    right: 0px;
    z-index: 10;
    display: flex;
    justify-content: space-between;
    background: #fff;
    border-radius: 5px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
    padding: 5px;
    align-items: center;
    p {
      display: block;
      margin: 0 5px 0 5px;
    }
  }
`;

const StyledTitle = styled.h3<{ $editable?: boolean }>`
  ${({ $editable }) =>
    $editable &&
    `
    cursor: pointer;
  `}
`;

const CircleIconLabel = styled(Label)`
  i {
    margin: 0 !important;
  }
`;

const ImpactAccordion = styled(Accordion)`
  &&& {
    background: #f1f2f4;

    .title {
      user-select: none;
    }
  }
`;

const HeaderImpactMeasure = ({
  icon,
  title,
  value,
  impactType,
  impactName,
  impactEffect,
}: {
  icon?: string;
  title: string;
  value: string | number | null;
  impactType?: OpenAPI.Schemas["OrganisationImpact"]["type"];
  impactName: string;
  impactDescription?: string;
  impactEffect?: OpenAPI.Schemas["OrganisationImpact"]["effect"];
}) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  let usingValue;
  let usingTitle;

  const usesImpactStatementSystem =
    user?.ownerOrganisation?.enabledFeatures?.includes("impactStatements");
  if (usesImpactStatementSystem) {
    const orgImpact = {
      type: impactType,
      effect: impactEffect,
    } as OrganisationImpact;
    usingValue = formatImpact(orgImpact, {
      value: value ?? "",
    } as LaneImpactInput);
    usingTitle = "";
  } else {
    usingValue = value;
    usingTitle = impactType === "time" ? "" : title;

    if (typeof usingValue === "number") {
      usingValue = usingValue.toLocaleString();
    }
    if (!usingValue) {
      usingValue = "None";
      usingTitle = "";
    }
  }

  const annualImpactValue = (impact: LaneImpactInput) => {
    if (usesImpactStatementSystem) {
      return formatImpact(impact.ownerImpact, impact, { annually: true });
    }

    if (typeof impact?.value === "string") {
      return impact.value;
    }
    const frequency = impact?.frequency ?? "annually";
    const numericValue = Number(impact?.value ?? 0);
    const annual = (impactMultipliers[frequency] ?? 1) * numericValue;
    if (impact?.ownerImpact?.type === "money") {
      return `£${annual}`;
    }
    return annual;
  };

  const generateImpactText = (impact: LaneImpactInput) => {
    const effect = impact?.ownerImpact?.effect;
    const effectText =
      effect === "cost" ? "cost"
      : effect === "saving" ? "saving"
      : "impact";
    let text = `The ${t("generic.ideas")} in this lane will have a total ${effectText} of `;
    if (usesImpactStatementSystem) {
      text = `The ${t("generic.ideas")} in this lane will have a total ${effectText} of up to `;
    }
    if (usesImpactStatementSystem) {
      text += formatImpact(impact.ownerImpact, impact);
    } else {
      if (impact?.ownerImpact?.type === "time") {
        text += `${impact?.value} hours`;
      } else if (impact?.ownerImpact?.type === "money") {
        text += `£${impact?.value}`;
      } else {
        text += `"${impact?.value}"`;
      }
    }

    if (!impact?.frequency || impact?.frequency === "one-off") {
      text += " once";
    } else {
      text += ` ${impact?.frequency}`;
      if (impact?.frequency !== "annually") {
        text += ` (${annualImpactValue(impact)} annually)`;
      }
    }
    text += ".";

    return (
      <>
        <p>
          <strong>{impact?.ownerImpact?.name}</strong>
        </p>
        <p>{text}</p>
        {impact?.justification ?
          <i>{impact?.justification}</i>
        : null}
      </>
    );
  };

  return (
    <li style={{ paddingLeft: 2 }}>
      <Popup
        content={generateImpactText({
          value: value ?? "",
          frequency: "annually",
          ownerImpact: {
            name: impactName,
            type: impactType,
            effect: impactEffect,
          } as OrganisationImpact,
        } as LaneImpactInput)}
        trigger={
          <div>
            <span>
              {icon ?
                <Icon size="small" name={icon as SemanticICONS} />
              : null}
              {impactName}
              {" - "}
              {impactEffect === "cost" ?
                "Costs "
              : impactEffect === "saving" ?
                "Saving "
              : ""}
              {usesImpactStatementSystem ?
                impactEffect ?
                  "up to "
                : "Up to "
              : ""}
              {usingValue}
            </span>
            <span>{` ${impactType === "money" ? "" : usingTitle}`}</span>
          </div>
        }
      />
    </li>
  );
};

type ICustomLaneHeader = {
  dragRef: React.RefObject<HTMLDivElement | null>;
  lane: ILaneData;
};

const CustomLaneHeader = ({ dragRef, lane }: ICustomLaneHeader) => {
  const {
    forId,
    canEditProjectBoard,
    selectIdeas,
    selectedIdeas,
    isSelecting,
    projectWebsocket,
  } = useContext(ProjectBoardContext);

  const user = useAppSelector((state) => state.user);

  const { id, title, description, metaType, cards } = lane;
  const allIdeasSelected =
    cards.length > 0 &&
    cards.every(
      (card) =>
        !!selectedIdeas.find(
          (selectedIdea) => selectedIdea._id === card.idea._id,
        ),
    );

  const onSelect = () => {
    selectIdeas(
      lane.cards.map((card) => card.idea),
      !allIdeasSelected,
    );
  };

  const [editingLaneName, setEditingLaneName] = useState(false);
  const [newLaneName, setNewLaneName] = useState(title ?? "");
  const [savingNewLaneName, setSavingNewLaneName] = useState(false);

  const queryParams = useIdeaQueryParams("board");
  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [forId, queryParams], {
      select: (data) => data.laneImpactMeasures?.[lane.id] ?? [],
    }),
  );
  const laneImpactMeasures = ideaImpactsQuery.data ?? [];

  const [editingLaneDescription, setEditingLaneDescription] = useState(false);
  const [newLaneDescription, setNewLaneDescription] = useState(
    description ?? "",
  );
  const [savingNewLaneDescription, setSavingNewLaneDescription] =
    useState(false);

  const { t } = useTranslation();

  const metaTypeInformation: Record<
    "notProgressing" | "completed" | "resolved",
    { color: SemanticCOLORS; icon: SemanticICONS; text: string }
  > = {
    notProgressing: {
      color: "orange",
      icon: "exclamation circle",
      text: `${t("common:capitalise", { key: "generic.ideas" })} not progressing`,
    },
    completed: {
      color: "teal",
      icon: "check circle",
      text: `Completed ${t("generic.ideas")}`,
    },
    resolved: {
      color: "blue",
      icon: "check circle",
      text: `Resolved ${t("generic.ideas")}`,
    },
  };

  const updateLane = (
    data: Partial<OpenAPI.Schemas["ProjectLane"]>,
    onComplete = () => {},
    onError = () => {},
  ) => {
    api.boards.updateProjectLane(
      forId,
      id,
      data as ProjectBoardLaneUpdateBody,
      (updatedLane) => {
        if (id === "default") {
          api.setQueryData(api.challenges.get, [forId], (prevChallenge) => ({
            ...(prevChallenge || {}),
            projectBoard: {
              ...(prevChallenge?.projectBoard || {}),
              defaultLane: updatedLane,
            },
          }));
        } else {
          api.invalidateQueries(api.boards.getProjectLanes, [forId]);
        }
        projectWebsocket?.current?.publish?.("lanesUpdated");
        onComplete();
      },
      (err) => {
        toast.error(err.message);
        onError();
      },
    );
  };

  const saveNewLaneName = () => {
    if (newLaneName !== title) {
      setSavingNewLaneName(true);
      updateLane(
        { name: newLaneName },
        () => {
          setEditingLaneName(false);
          setSavingNewLaneName(false);
        },
        () => {
          setEditingLaneName(false);
          setSavingNewLaneName(false);
        },
      );
    } else {
      setEditingLaneName(false);
    }
  };

  const saveNewLaneDescription = () => {
    if (newLaneDescription !== description) {
      setSavingNewLaneDescription(true);
      updateLane(
        { description: newLaneDescription },
        () => {
          setEditingLaneDescription(false);
          setSavingNewLaneDescription(false);
        },
        () => {
          setEditingLaneDescription(false);
          setSavingNewLaneDescription(false);
        },
      );
    } else {
      setEditingLaneDescription(false);
    }
  };

  const reorderLaneIdeas = (sort: string, sortDirection: string) => {
    api.boards.reorderLaneIdeas(
      forId,
      id,
      sort,
      sortDirection,
      () => {
        projectWebsocket?.current?.publish?.("ideasUpdated");
        api.invalidateQueries(api.challenges.getIdeas, [forId]);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  };

  const headerProps =
    isSelecting ? { isSelecting: true, onClick: onSelect } : {};
  const metaTypeInfo =
    metaType ?
      metaTypeInformation[metaType as keyof typeof metaTypeInformation]
    : undefined;

  return (
    <Header {...headerProps}>
      <StyledLaneHeader ref={dragRef}>
        <HeaderContainer>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              width: "100%",
            }}
          >
            {!editingLaneName ?
              <Label style={{ display: "flex" }}>
                <Icon name="lightbulb" />
                {cards.length}
              </Label>
            : null}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
              }}
            >
              {id === "default" && !editingLaneName ?
                <Popup
                  content={
                    <div>
                      This is the default lane, it cannot be deleted or moved to
                      a different position.
                      {canEditProjectBoard && title === "Default" ?
                        <Label
                          size="small"
                          icon="info"
                          content="You can edit the name of this lane"
                        />
                      : null}
                    </div>
                  }
                  trigger={
                    <div>
                      <CircleIconLabel
                        color={
                          canEditProjectBoard && title === "Default" ?
                            "yellow"
                          : "grey"
                        }
                        icon="lock"
                        circular
                      />
                    </div>
                  }
                />
              : null}

              {editingLaneName ?
                <Input
                  value={newLaneName}
                  onChange={(e) => setNewLaneName(e.target.value)}
                  autoFocus
                  size="small"
                  fluid
                  style={{ width: "100%", height: 28 }}
                  action={
                    <Button
                      primary
                      icon="check"
                      onClick={() => saveNewLaneName()}
                      size="small"
                      loading={savingNewLaneName}
                    />
                  }
                />
              : <Popup
                  trigger={
                    <StyledTitle
                      $editable={canEditProjectBoard}
                      onClick={() =>
                        canEditProjectBoard && setEditingLaneName(true)
                      }
                    >
                      {title}
                    </StyledTitle>
                  }
                  content={title}
                />
              }
            </div>
          </div>
        </HeaderContainer>

        {canEditProjectBoard ?
          <DropdownLaneSettings
            lane={lane}
            editLaneName={() => setEditingLaneName(true)}
            editLaneDescription={() => setEditingLaneDescription(true)}
            updateLane={updateLane}
            reorderLaneIdeas={reorderLaneIdeas}
            trigger={
              <InvisibleButton icon size="mini">
                <Icon name="ellipsis horizontal" />
              </InvisibleButton>
            }
          />
        : null}
      </StyledLaneHeader>

      {metaTypeInfo ?
        <MetaInfo
          icon={metaTypeInfo.icon}
          text={metaTypeInfo.text}
          color={metaTypeInfo.color}
        />
      : null}

      {editingLaneDescription ?
        <div>
          <Form>
            <TextArea
              placeholder="Enter a description for this lane.."
              value={newLaneDescription}
              onChange={(e) => setNewLaneDescription(e.target.value)}
              autoFocus
            />
            <Button
              primary
              content="Save"
              icon="check"
              onClick={() => saveNewLaneDescription()}
              size="mini"
              loading={savingNewLaneDescription}
              style={{ marginTop: 5 }}
              fluid
            />
          </Form>
        </div>
      : description ?
        <LaneDescription>{description}</LaneDescription>
      : null}

      {isSelecting && cards?.length ?
        <div className="selection-box">
          <p>Select all {t("generic.ideas")} in this lane</p>
          <Checkbox isSelected={allIdeasSelected} compact />
        </div>
      : null}

      <ImpactAccordion
        styled
        fluid
        style={
          laneImpactMeasures.filter((m) => !!m.laneTotalImpactValue).length ?
            {}
          : { display: "none" }
        }
        panels={[
          {
            key: "total-impact",
            title: "Annual impact",
            content: (_content: string, { active }: { active: boolean }) => (
              <div style={{ paddingRight: 10 }}>
                <ul
                  style={{
                    display: active ? "block" : "none",
                    marginTop: 0,
                    paddingLeft: 31,
                    paddingRight: 21,
                    maxHeight: 100,
                    overflow: "auto",
                  }}
                >
                  {laneImpactMeasures
                    .filter((m) => !!m.laneTotalImpactValue)
                    .map((impactMeasure) => {
                      if (impactMeasure.type === "dropdown") {
                        return null;
                      }
                      return (
                        <HeaderImpactMeasure
                          key={impactMeasure._id}
                          icon={impactMeasure.icon ?? undefined}
                          title={impactMeasure.name ?? ""}
                          value={impactMeasure.laneTotalImpactValue ?? 0}
                          impactType={impactMeasure.type}
                          impactName={impactMeasure.name ?? ""}
                          impactDescription={impactMeasure.description ?? ""}
                          impactEffect={impactMeasure.effect}
                        />
                      );
                    })}
                </ul>
              </div>
            ),
          },
        ]}
      />
    </Header>
  );
};

export default CustomLaneHeader;
